import React, { useState, useEffect } from "react";
import axios from "axios";
import { ThreeDots } from "react-loader-spinner";
import Table from "./overview/Table";
import { useNavigate } from "react-router-dom";

function ServiceBrowse() {
  const apiUrl = `${process.env.REACT_APP_FAST_URL}/service.php`;
  const [serviceDataList, setServiceDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const navigate = useNavigate();

  const services = async () => {
    try {
      setIsLoading(true);
      const result = await axios.get(apiUrl, {
        params: {
          per_page: 100
        },
      });
      setServiceDataList(result.data);
      setIsLoading(false);
    } catch (err) {
      console.log(err.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    services();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = (productId) => { // Changed parameter name to productId
    const service = serviceDataList.find(item => item.id === productId); // Find by product_id
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const handleOrderNow = () => {
    if (selectedService) {
      // Navigate to order form with service pre-selected
      // Pass both service_id (for external API) and id (for product_id)
      const orderUrl = `/order/new?service=${selectedService.service_id}&productId=${selectedService.id || ''}&category=${selectedService.category || ''}`;
      console.log('=== NAVIGATION DEBUG ===');
      console.log('Selected service:', selectedService);
      console.log('Navigating to:', orderUrl);
      console.log('Service ID:', selectedService.service_id);
      console.log('Product ID (derived):', selectedService.id || '');
      console.log('Category:', selectedService.category || '');
      console.log('========================');

      // Small delay to ensure debugging shows up
      setTimeout(() => {
        navigate(orderUrl);
      }, 100);
    }
  };

  const getData = () => {
    return serviceDataList.map((item) => ({
      id: item.id, // Use product_id as the unique identifier for the table
      service_id: item.service_id, // Keep service_id available for the order URL
      name: item.name,
      type: item.type,
      status: item.status,
      price: item.price,
      description: item.description,
      min_order: item.min_order || 1,
      max_order: item.max_order || 1000,
      product_id: item.product_id, // Also explicitly include product_id
      category: item.category, // Also explicitly include category
    }));
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Service",
        accessor: "name",
        Cell: ({ cell }) => (
          <div className="font-medium text-gray-900">
            {cell.row.original.name}
          </div>
        ),
      },
      {
        Header: "Type",
        accessor: "type",
      },
      {
        Header: "Price per 1000",
        accessor: "price",
        Cell: ({ cell }) => (
          <div className="font-semibold text-green-600">
            ${cell.row.original.price}
          </div>
        ),
      },
      {
        Header: "Min Order",
        accessor: "min_order",
      },
      {
        Header: "Max Order",
        accessor: "max_order",
      },
      {
        Header: "Description",
        accessor: "description",
        Cell: ({ cell }) => (
          <div className="max-w-xs truncate" title={cell.row.original.description}>
            {cell.row.original.description}
          </div>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ cell }) => (
          <div className="flex space-x-2">
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm transition duration-200"
              onClick={() => openModal(cell.row.original.id)}
            >
              View
            </button>
          </div>
        ),
      },
    ],
    [openModal]
  );

  const data = React.useMemo(() => getData(), [serviceDataList]);

  return (
    <div className="text-gray-900">
      <main className="w-full mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="mb-6">
          <h2 className="text-2xl text-gray-600 font-semibold">
            Browse Services
          </h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center mt-20">
            <ThreeDots
              height="80"
              width="80"
              radius="9"
              color="#4fa94d"
              ariaLabel="three-dots-loading"
              visible={true}
            />
          </div>
        ) : data.length > 0 ? (
          <div className="mt-4">
            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
              <Table columns={columns} data={data} />
            </div>
            <div className="mt-4 text-sm text-gray-600">
              Total services: {serviceDataList.length}
            </div>
          </div>
        ) : (
          <div className="mt-8 max-w-md mx-auto bg-white p-8 border border-gray-300 rounded-md shadow-sm text-center">
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              No services found
            </h3>
            <p className="text-gray-500">
              No services are currently available
            </p>
          </div>
        )}
      </main>

      {/* Service Details Modal */}
      {isModalOpen && selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-gray-900">
                  {selectedService.name}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              {/* Service Details */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Service ID</label>
                    <p className="text-lg font-semibold text-blue-600">{selectedService.service_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Product ID</label>
                    <p className="text-lg font-semibold text-purple-600">{selectedService.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <p className="text-lg">{selectedService.type}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Price per 1000</label>
                    <p className="text-lg font-semibold text-green-600">${selectedService.price}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`px-2 py-1 rounded-full text-sm ${
                      selectedService.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedService.status}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Min Order</label>
                    <p className="text-lg">{selectedService.min_order || 1}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Order</label>
                    <p className="text-lg">{selectedService.max_order || 1000}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedService.description}</p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition duration-200"
                >
                  Close
                </button>
                <button
                  onClick={handleOrderNow}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 font-medium"
                >
                  Order Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServiceBrowse;
